<script>
import IconWithBackground from '@modules/crm/components/icon-with-background/icon-with-background.vue'
import {
  sentimentMeta,
  normalizeSentimento,
} from '@modules/crm/constants/Sentiment'
import {
  toScore,
  formatScorePtBr,
  scoreColor,
} from '@modules/crm/utils/transcriptScore'

const SCORE_SUFFIX = '/10'

const ICON_SEM_ANALISE = 'ai-sparkle'

// Destaque do cabeçalho: carinha do sentimento + análise da IA + nota da reunião.
// A nota é o project_score (0-10) — a mesma da aba Avaliação e da coluna "Nota"
// do histórico — e usa a cor da própria faixa, não a do sentimento: reunião com
// clima positivo e nota baixa não pode aparecer em verde.
export default {
  name: 'TranscriptSentimentCard',
  components: { IconWithBackground },
  props: {
    sentimento: { type: String, default: null },
    justificativa: { type: String, default: '' },
    alinhamento: { type: String, default: '' },
    score: { type: [Number, String], default: null },
  },
  computed: {
    // O backend pode devolver o rótulo em inglês (positive/neutral/negative).
    sentimentoKey() {
      return normalizeSentimento(this.sentimento)
    },
    meta() {
      return sentimentMeta(this.sentimentoKey)
    },
    hasSentimento() {
      return Boolean(this.sentimentoKey)
    },
    hasScore() {
      return toScore(this.score) !== null
    },
    title() {
      if (this.hasSentimento) {
        return 'Sentimento ' + this.meta.label.toLowerCase()
      }
      // A nota é independente do sentimento — sem essa distinção o texto mentiria
      // ao dizer que a nota não saiu quando ela já está ali do lado.
      if (this.hasScore) return 'Sentimento ainda não disponível'
      return 'Sentimento e nota ainda não disponíveis'
    },
    subtitle() {
      return (
        this.justificativa ||
        'A análise da IA aparece aqui assim que for gerada.'
      )
    },
    alinhamentoLabel() {
      if (!this.alinhamento) return ''
      return 'Alinhamento aos objetivos: ' + this.alinhamento
    },
    // IconWithBackground resolve a cor por getComputedStyle(--<accent>), então
    // recebe o nome do token — não o `var(--x)` do cssVar.
    accentToken() {
      return this.meta.accent
    },
    iconName() {
      return this.hasSentimento ? this.meta.icon : ICON_SEM_ANALISE
    },
    titleStyle() {
      return { color: this.meta.cssVar }
    },
    // Com sentimento, o card é tintado na cor dele (mesmo padrão color-mix do
    // scoreColorSoft). Sem sentimento, não devolve nada: valem os tokens neutros
    // do <style> (--neutral-50 + --border-hairline), iguais aos do card de
    // cliente/projeto.
    cardStyle() {
      if (!this.hasSentimento) return {}
      const color = this.meta.cssVar
      return {
        background: 'color-mix(in srgb, ' + color + ' 8%, #FFFFFF)',
        borderColor: 'color-mix(in srgb, ' + color + ' 30%, #FFFFFF)',
      }
    },
    scoreStyle() {
      if (!this.hasSentimento) return {}
      return {
        borderColor:
          'color-mix(in srgb, ' + this.meta.cssVar + ' 30%, #FFFFFF)',
      }
    },
    notaFormatada() {
      return formatScorePtBr(this.score)
    },
    notaColor() {
      return scoreColor(this.score)
    },
    scoreSuffix() {
      return SCORE_SUFFIX
    },
  },
}
</script>

<template>
  <div
    class="transcript-sentiment-card az-card-bordered no-padding no-shadow d-flex flex-column flex-sm-row"
    :style="cardStyle"
  >
    <div
      class="transcript-sentiment-card__content d-flex align-center az-gap pa-4 flex-grow-1"
    >
      <IconWithBackground
        rounded
        class="flex-shrink-0"
        :icon="iconName"
        :container-size="48"
        :icon-size="28"
        :color="accentToken"
        :opacity="0.15"
      />
      <div class="d-flex flex-column az-gap-xxsmall">
        <span class="az-ds-card-title" :style="titleStyle">{{ title }}</span>
        <span class="az-ds-body">{{ subtitle }}</span>
        <span v-if="alinhamentoLabel" class="az-ds-aux">
          {{ alinhamentoLabel }}
        </span>
      </div>
    </div>

    <div
      class="transcript-sentiment-card__score d-flex flex-column align-center justify-center flex-shrink-0 px-6 py-4"
      :style="scoreStyle"
    >
      <span class="d-flex align-baseline">
        <span class="az-ds-kpi" :style="{ color: notaColor }">
          {{ notaFormatada }}
        </span>
        <span v-if="hasScore" class="az-ds-aux" :style="{ color: notaColor }">
          {{ scoreSuffix }}
        </span>
      </span>
      <span class="az-ds-label">Nota</span>
    </div>
  </div>
</template>

<style lang="sass" scoped>
// Borda, raio e sombra vêm do az-card-bordered. Sobra o fundo cinza do estado
// sem análise (o card do DS é branco); havendo sentimento, o cardStyle pinta
// fundo e borda por cima com o tint da cor.
.transcript-sentiment-card
  background: var(--neutral-50)

  // Deixa a análise da IA quebrar linha em vez de estourar o card. Só na coluna
  // de conteúdo: no bloco da nota isso espremia o número letra a letra.
  &__content
    min-width: 0

  &__score
    white-space: nowrap
    border-top: 1px solid var(--border-hairline)

    @media (min-width: 600px)
      border-top: 0
      border-left: 1px solid var(--border-hairline)
</style>
